import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MusicNotesSimple, Storefront, ArrowLeft, CheckCircle, ShieldCheck, Receipt, PlayCircle, Star } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NegociosInfo() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    axios.get(`${API}/business/plans`).then(r => setPlans(r.data?.plans || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5" data-testid="biz-info-back-home">
          <ArrowLeft size={16} weight="bold" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-full bg-[#FFB800] flex items-center justify-center">
            <MusicNotesSimple weight="fill" size={22} className="text-black" />
          </div>
          <span className="font-display font-bold text-2xl">Tu MusiKa <span className="text-[#B4F34A]">Negocios</span></span>
        </Link>

        <div className="flex items-center gap-2 mb-4">
          <Storefront weight="fill" size={28} className="text-[#B4F34A]" />
          <p className="text-sm uppercase tracking-widest text-[#B4F34A]">Para tu negocio</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-6">
          Música legal para tu restaurante,<br />
          <span className="text-[#FFB800]">bar o cafetería</span>
        </h1>
        <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-10 max-w-3xl">
          Evita multas millonarias por reproducir música con derechos de autor.
          Accedes a un catálogo enorme y variado de baladas, cumbias, rancheras, corridos, rock, salsa, reggaeton, banda y más —
          hecha por talentos mexicanos y latinoamericanos.
        </p>

        {/* PROCESS — 4 steps */}
        <h2 className="font-display text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-[#FFB800] rounded-full" /> Cómo funciona en 4 pasos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14">
          {[
            { icon: Storefront, t: "1. Elige tu plan y registra tu negocio", d: "Selecciona Básico, Pro o Premium según el tamaño de tu negocio. Llena tus datos: nombre del negocio, domicilio, teléfono y contraseña." },
            { icon: Receipt, t: "2. Realiza tu pago", d: "Transfiere el monto de tu plan por BBVA. Sube la foto del comprobante y nosotros validamos en horas. Pago anual: 20% de descuento." },
            { icon: ShieldCheck, t: "3. Recibe tu Número de Comercio Autorizado", d: "Te asignamos un código único (TM-XXXXXX) que comprueba que pagas el derecho de uso. Queda asociado a tu red WiFi para evitar uso indebido." },
            { icon: PlayCircle, t: "4. Conecta tu música y disfruta", d: "En tu panel encontrarás un enlace que abres en una tablet o PC conectada a tu equipo de sonido. ¡Listo, sin multas, con música original!" },
          ].map((s) => (
            <div key={s.t} className="rounded-2xl bg-[#121212] border border-white/5 p-5">
              <s.icon size={26} weight="duotone" className="text-[#B4F34A] mb-3" />
              <p className="font-display font-bold text-lg">{s.t}</p>
              <p className="text-sm text-white/60 mt-1 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        {/* PRICING PLANS */}
        <h2 className="font-display text-3xl font-bold mb-2 flex items-center gap-2">
          <span className="w-1 h-7 bg-[#B4F34A] rounded-full" /> Planes y precios
        </h2>
        <p className="text-white/50 mb-8 text-sm">Todos los precios en MXN · Cancela cuando quieras · 20% de descuento al pagar anual</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
          {plans.map((p, i) => (
            <div
              key={p.key}
              className={`rounded-3xl p-6 border ${i === 1 ? "bg-gradient-to-b from-[#0e1d10] to-[#0a0a0a] border-[#B4F34A]/40" : "bg-[#121212] border-white/10"} relative`}
              data-testid={`plan-card-${p.key}`}
            >
              {i === 1 && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#B4F34A] text-black text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                  <Star size={10} weight="fill" /> Más popular
                </div>
              )}
              <p className="font-display text-xl font-bold mb-1">{p.name}</p>
              <p className="text-xs text-white/50 mb-4">{p.tagline}</p>
              <p className="font-display font-black text-4xl text-[#FFB800]">${p.price_monthly}<span className="text-base font-normal text-white/60"> MXN/mes</span></p>
              {p.key === "premium" && p.volume_price && (
                <p className="text-xs text-[#B4F34A] mt-1">${p.volume_price}/sucursal con {p.volume_threshold}+</p>
              )}
              <p className="text-xs text-white/40 mt-3 mb-4 leading-relaxed">{p.ideal_for}</p>
              <ul className="space-y-2 mb-5">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-white/80">
                    <CheckCircle weight="fill" size={16} className="text-[#B4F34A] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/negocios?plan=${p.key}`}
                className={`w-full inline-flex items-center justify-center py-3 rounded-full font-bold text-sm active:scale-95 transition-all ${i === 1 ? "bg-[#B4F34A] hover:bg-[#A0E03A] text-black" : "bg-white/10 hover:bg-white/15 text-white"}`}
                data-testid={`plan-cta-${p.key}`}
              >
                Contratar {p.name.replace("Plan ", "")}
              </Link>
            </div>
          ))}
        </div>

        {/* SECURITY NOTE */}
        <div className="rounded-2xl bg-[#FF4B00]/5 border border-[#FF4B00]/20 p-6 mb-10">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck weight="fill" size={22} className="text-[#FF4B00]" />
            <p className="font-semibold">Protegemos tu Número de Comercio Autorizado</p>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Tu número queda asociado a la red WiFi de tu negocio desde la primera reproducción.
            Si alguien intenta usarlo en otro lugar, el sistema lo bloquea automáticamente y nos notifica.
            Si cambias de ubicación legítimamente, contáctanos por WhatsApp para liberar tu cuenta.
          </p>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Link to="/negocios" className="flex-1 py-4 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-semibold text-center active:scale-95 transition-all" data-testid="biz-info-register">
            Registrar mi negocio
          </Link>
          <Link to="/negocios/login" className="flex-1 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-center" data-testid="biz-info-login">
            Ya tengo cuenta
          </Link>
        </div>
        <p className="text-center text-xs text-white/40">
          <Link to="/negocios/terminos" className="underline hover:text-white/70">Ver Términos del Servicio para Comercios</Link>
        </p>
      </div>
    </div>
  );
}
